<script setup lang="ts">
/**
 * 用量计量条（额度页）。
 *
 * 单独成组件（原实现里是 `quota/page.tsx` 的私有函数）是因为 Vue 的
 * `defineProps` 是编译期宏，没法像 React 那样在一个文件的私有函数上用——
 * 只能在真正的 SFC 里声明 props。
 *
 * v1.3.0 起它吃的是**服务端算好的百分比**：`remainingPercent`（0-100 整数）、
 * `unlimited`，以及"这个百分比属于哪一维"的 `scope` 与它的 `resetAt`。
 * 组件里再没有一次除法——唯一的百分比来源是服务端，界面不可能算出第二个值。
 *
 * 两个分支的差别是刻意的：
 * - 不限额度：进度条宽度固定 0，文案写"不限"。给一个"看起来快满了"的条会平白制造焦虑，
 *   而且此时日/月维度本身没有意义，所以不显示重置倒计时。
 * - 有限额度：标题与被跟踪的那一维一致（今日/本月），下面一行说清"按哪一维算、何时重置"。
 *
 * `now` 由页面每 60 秒推进一次：重置倒计时必须自己走，否则把这一页开着过夜，
 * 它会一直说"3 小时后重置"。
 */

import { computed } from 'vue';
import type { QuotaScope } from '@spirit/contracts';

import {
  QUOTA_WARNING_THRESHOLD_PERCENT,
  UNLIMITED_QUOTA_HINT,
  formatResetCountdown,
  quotaScopeLabelOf,
  quotaScopeTitleOf,
} from '../../lib/quota-display';

const props = defineProps<{
  unlimited: boolean;
  scope: QuotaScope;
  remainingPercent: number;
  resetAt: string;
  /** 当前时刻（毫秒）；由调用方的计时器推进。 */
  now: number;
}>();

const title = computed(() => quotaScopeTitleOf(props.scope));

/** 百分比是进度条的宽度来源，越界值会画到容器外面。 */
const percent = computed(() => Math.max(0, Math.min(100, Math.round(props.remainingPercent))));
const warning = computed(() => props.unlimited === false && percent.value < QUOTA_WARNING_THRESHOLD_PERCENT);

const hint = computed(() =>
  props.unlimited
    ? UNLIMITED_QUOTA_HINT
    : `${quotaScopeLabelOf(props.scope)} · ${formatResetCountdown(props.resetAt, props.now)}`,
);
</script>

<template>
  <div>
    <div class="flex items-baseline justify-between gap-2">
      <span class="text-sm font-medium text-slate-800">{{ title }}</span>
      <span v-if="props.unlimited" class="text-sm font-medium text-brand-700">不限</span>
      <span
        v-else
        class="text-sm font-medium tabular-nums"
        :class="warning ? 'text-safety-600' : 'text-slate-700'"
      >
        剩余 {{ percent }}%
      </span>
    </div>

    <!-- 不限额度：静态的空条，只是保持版面一致 -->
    <div
      v-if="props.unlimited"
      role="progressbar"
      :aria-label="title"
      aria-valuetext="不限额度"
      class="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-brand-100"
    >
      <div class="h-full w-0 rounded-full bg-brand-500" />
    </div>

    <div
      v-else
      role="progressbar"
      :aria-label="title"
      :aria-valuemin="0"
      :aria-valuemax="100"
      :aria-valuenow="percent"
      :aria-valuetext="`剩余 ${percent}%`"
      class="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100"
    >
      <div
        class="h-full rounded-full transition-all"
        :class="warning ? 'bg-safety-500' : 'bg-brand-500'"
        :style="{ width: `${percent}%` }"
      />
    </div>

    <p class="mt-1.5 text-xs text-slate-500">{{ hint }}</p>
  </div>
</template>
